import React from 'react';
import { array, bool, func, string } from 'prop-types';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { propTypes, REVIEW_TYPE_OF_PROVIDER, REVIEW_TYPE_OF_CUSTOMER } from '../../util/types';
import { createSlug } from '../../util/urlHelpers';
import { userDisplayNameAsString } from '../../util/data';
import { createResourceLocatorString } from '../../util/routes';

import {
  Avatar,
  EmptyState,
  LoadingPlaceholder,
  NamedLink,
  Notice,
  ReviewRating,
} from '../../components';

import css from './FeedbackTabContent.module.css';

// Calculate time ago string
const formatDate = (date, intl) => {
  const now = new Date();
  const reviewDate = new Date(date);
  const diffMs = now - reviewDate;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return intl.formatMessage({ id: 'FeedbackTabContent.today' });
  } else if (diffDays === 1) {
    return intl.formatMessage({ id: 'FeedbackTabContent.yesterday' });
  } else if (diffDays < 7) {
    return intl.formatMessage({ id: 'FeedbackTabContent.daysAgo' }, { days: diffDays });
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return intl.formatMessage({ id: 'FeedbackTabContent.weeksAgo' }, { weeks });
  } else if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return intl.formatMessage({ id: 'FeedbackTabContent.monthsAgo' }, { months });
  } else {
    const years = Math.floor(diffDays / 365);
    return intl.formatMessage({ id: 'FeedbackTabContent.yearsAgo' }, { years });
  }
};

const StarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="m12 3.5 2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8-4.3-4.1 5.9-.9L12 3.5Z" />
  </svg>
);

const FILTERS = ['all', 'seller', 'buyer'];

// Review item component
const ReviewItem = ({ review, intl }) => {
  const { author, listing, attributes } = review;
  const { rating, content, createdAt } = attributes;

  const authorName = userDisplayNameAsString(author, 'Anonymous');
  const dateString = formatDate(createdAt, intl);

  // Get listing info if available
  const listingTitle = listing?.attributes?.title;
  const listingId = listing?.id?.uuid;

  return (
    <li className={css.reviewItem}>
      <Avatar className={css.authorAvatar} user={author} disableProfileLink />
      <div className={css.reviewBody}>
        <div className={css.reviewHeader}>
          <span className={css.authorName}>{authorName}</span>
          <span className={css.reviewDate}>{dateString}</span>
        </div>
        {rating ? (
          <ReviewRating className={css.stars} reviewStarClassName={css.star} rating={rating} />
        ) : null}
        {content && <p className={css.reviewContent}>{content}</p>}
        {listingTitle && listingId && (
          <NamedLink
            className={css.listingLink}
            name="ListingPage"
            params={{ id: listingId, slug: createSlug(listingTitle) }}
          >
            {listingTitle}
          </NamedLink>
        )}
      </div>
    </li>
  );
};

const FeedbackTabContent = props => {
  const {
    reviews,
    queryReviewsInProgress,
    queryReviewsError,
    activeReviewType,
    onReviewTypeChange,
    className,
    rootClassName,
    username,
    routeConfiguration,
  } = props;

  const intl = useIntl();
  const history = useHistory();

  // Filter reviews based on active type
  const filteredReviews =
    activeReviewType === 'all'
      ? reviews
      : activeReviewType === 'seller'
      ? reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER)
      : reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);

  // Count reviews by type
  const counts = {
    all: reviews.length,
    seller: reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER).length,
    buyer: reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER).length,
  };

  const handleTypeChange = type => {
    onReviewTypeChange(type);
    history.push(
      createResourceLocatorString(
        'ProfilePage',
        routeConfiguration,
        { username },
        { tab: 'feedback', reviewType: type }
      )
    );
  };

  const classes = classNames(rootClassName || css.root, className);

  // One state at a time: loading, error, no reviews at all, or the list (with its filters)
  const content = queryReviewsInProgress ? (
    <LoadingPlaceholder count={3} />
  ) : queryReviewsError ? (
    <Notice tone="error">
      <p>
        <FormattedMessage id="FeedbackTabContent.queryError" />
      </p>
    </Notice>
  ) : reviews.length === 0 ? (
    <EmptyState
      icon={<StarIcon />}
      title={intl.formatMessage({ id: 'FeedbackTabContent.emptyTitle' })}
      text={intl.formatMessage({ id: 'FeedbackTabContent.emptyText' })}
    />
  ) : (
    <>
      <div
        className={css.subTabs}
        role="tablist"
        aria-label={intl.formatMessage({ id: 'FeedbackTabContent.filterLabel' })}
      >
        {FILTERS.map(type => (
          <button
            key={type}
            type="button"
            role="tab"
            aria-selected={activeReviewType === type}
            className={classNames(css.subTab, { [css.subTabActive]: activeReviewType === type })}
            onClick={() => handleTypeChange(type)}
          >
            <FormattedMessage id={`FeedbackTabContent.${type}`} />
            <span className={css.subTabCount}>{counts[type]}</span>
          </button>
        ))}
      </div>
      {filteredReviews.length > 0 ? (
        <ul className={css.reviewsList}>
          {filteredReviews.map(review => (
            <ReviewItem key={review.id.uuid} review={review} intl={intl} />
          ))}
        </ul>
      ) : (
        <p className={css.noReviews}>
          <FormattedMessage id="FeedbackTabContent.noReviewsOfType" />
        </p>
      )}
    </>
  );

  return <div className={classes}>{content}</div>;
};

FeedbackTabContent.defaultProps = {
  className: null,
  rootClassName: null,
  reviews: [],
  queryReviewsInProgress: false,
  queryReviewsError: null,
  activeReviewType: 'all',
};

FeedbackTabContent.propTypes = {
  className: string,
  rootClassName: string,
  reviews: array,
  queryReviewsInProgress: bool,
  queryReviewsError: propTypes.error,
  activeReviewType: string,
  onReviewTypeChange: func.isRequired,
  username: string.isRequired,
  routeConfiguration: array.isRequired,
};

export default FeedbackTabContent;
